/* eslint-disable camelcase */
/**
 * Wire-shape -> ToolTraceEvent codec — Phase 1 Slice B of the wire-codec
 * boundary migration.
 *
 * The orchestrator's BE (`chat/orchestrator/agent.py`) persists the raw
 * SSE wire shape into ``ChatMessage.orchestrator_trace``:
 *
 *   {"type": "tool_started", "tool": "...",
 *    "label_template": "...", "entities": [...], ...}
 *
 * The SAME events arrive two ways and used to be parsed by TWO shims:
 *   - the live SSE path (`api/chatStream.ts`) had an inline per-event
 *     case statement reading snake_case off the raw `fetch` body;
 *   - the replay path (`ChatTranscript.tsx` / Portal Chat Page) read the
 *     persisted JSONField, which the global axios interceptor had
 *     recursively camelized (`label_template` → `labelTemplate`).
 *
 * Phase 1 Slice B collapses both onto ONE codec: ``parseTraceEvent``
 * reads the discriminator + every field tolerant of BOTH spellings (via
 * the shared ``dualKey`` from ``@/api/codec``) and emits the canonical
 * FE-typed :type:`ToolTraceEvent` (camelCase — the shape the reducer
 * consumes). Feed it the axios-raw shape OR the SSE shape and it
 * produces identical output. That kills the dual-shape bug at the root
 * for the trace surface.
 *
 * Canonical output here is camelCase (NOT snake_case like the opaque
 * tool-result/card payloads) because ``ToolTraceEvent`` is a long-
 * established FE type with a reducer + renderer built on its camelCase
 * fields (`labelTemplate`, `argsSummary`, `parentId`). The codec's job
 * is to converge the two transports onto that existing contract.
 *
 * If a stored/streamed event doesn't match a known shape, it is dropped
 * (NOT thrown) — a single malformed event must not break the whole
 * transcript render.
 */
import { dualKey, isObject } from "@/api/codec";

import type { ToolTraceEvent, TraceEntity } from "./types";

const TS_EPOCH = 0;

const asString = (v: unknown): string | undefined =>
  typeof v === "string" ? v : undefined;

const asStringOr = (v: unknown, fallback: string): string =>
  typeof v === "string" ? v : fallback;

const asArray = (v: unknown): readonly unknown[] => (Array.isArray(v) ? v : []);

/**
 * Normalize a single trace entity tolerant of snake/camel
 * (`iso_date` ↔ `isoDate`). Emits the FE :type:`TraceEntity` (the
 * salience whitelist union); returns ``null`` for an out-of-whitelist
 * kind or a missing label so the caller can drop it.
 */
export const parseTraceEntity = (raw: unknown): TraceEntity | null => {
  if (!isObject(raw)) return null;
  const kind = raw.kind as TraceEntity["kind"];
  const label = asString(raw.label);
  if (!label) return null;
  switch (kind) {
    case "building":
      return {
        kind,
        label,
        bbl: asString(raw.bbl),
        bin: asString(raw.bin),
      };
    case "law":
    case "code_section":
    case "filing":
    case "violation":
      return { kind, label, ref: asStringOr(raw.ref, label) };
    case "dataset":
      return { kind, label, ref: asString(raw.ref) };
    case "deadline":
      return { kind, label, isoDate: asStringOr(dualKey(raw, "iso_date"), "") };
    case "period":
      return {
        kind,
        label,
        start: asStringOr(raw.start, ""),
        end: asStringOr(raw.end, ""),
      };
    default:
      return null;
  }
};

export const parseTraceEntities = (raw: unknown): readonly TraceEntity[] =>
  asArray(raw)
    .map(parseTraceEntity)
    .filter((e): e is TraceEntity => e !== null);

/**
 * Parse a single raw trace event (wire snake_case OR axios-camelized)
 * into a typed :type:`ToolTraceEvent`. Returns ``null`` on malformed
 * input so the caller can drop unknown shapes without crashing.
 *
 * This is the SINGLE consumer-boundary codec for trace events. Both the
 * SSE path (`chatStream.ts`) and the replay path route through it; do
 * NOT write a second per-event normalizer.
 *
 * Recognizes BOTH the BE wire shape (``type`` + snake_case) AND an
 * already-normalized FE shape (``kind`` + camelCase) — useful when
 * mixed-source histories surface during a deploy window.
 */
export const parseTraceEvent = (
  raw: unknown,
  fallbackTs: number,
): ToolTraceEvent | null => {
  if (!isObject(raw)) return null;

  // Already-normalized: hand back as-is (the reducer's exhaustiveness
  // check rejects anything genuinely unexpected).
  if (typeof raw.kind === "string") {
    return raw as unknown as ToolTraceEvent;
  }

  const type = asString(raw.type);
  if (!type) return null;

  // BE doesn't stamp ids on persisted events — synthesize a stable
  // surrogate that's SAME for the started/progress/completed/failed
  // events of one tool call, so the reducer's by-id fan-in lines up.
  // Phase 1 limitation: assumes one instance of each tool per turn; D2
  // multi-instance fan-out needs BE-stamped ids (Phase 2).
  const toolName = asString(raw.tool);
  const id = asString(raw.id) ?? toolName ?? `${type}-${fallbackTs}`;
  const ts = fallbackTs;

  switch (type) {
    case "planning": {
      const label =
        asString(dualKey(raw, "label")) ??
        asString(dualKey(raw, "rationale")) ??
        `Routing to ${asString(dualKey(raw, "shape")) ?? "playbook"}…`;
      return { id, ts, kind: "planning", label };
    }
    case "tool_started": {
      const tool = asString(raw.tool);
      if (!tool) return null;
      const argsSummary = dualKey(raw, "args_summary");
      return {
        id,
        ts,
        kind: "tool_started",
        tool,
        labelTemplate: asStringOr(dualKey(raw, "label_template"), tool),
        entities: parseTraceEntities(dualKey(raw, "entities")),
        argsSummary: isObject(argsSummary) ? argsSummary : undefined,
      };
    }
    case "tool_progress": {
      const tool = asString(raw.tool);
      if (!tool) return null;
      return {
        id,
        ts,
        kind: "tool_progress",
        parentId: asString(dualKey(raw, "parent_id")) ?? tool,
        labelTemplate: asStringOr(dualKey(raw, "label_template"), tool),
        entities: parseTraceEntities(dualKey(raw, "entities")),
      };
    }
    case "tool_completed": {
      const tool = asString(raw.tool);
      if (!tool) return null;
      return {
        id,
        ts,
        kind: "tool_completed",
        parentId: asString(dualKey(raw, "parent_id")) ?? tool,
        collapsedLabelTemplate: asStringOr(
          dualKey(raw, "collapsed_label"),
          `Checked ${tool}`,
        ),
        entities: parseTraceEntities(dualKey(raw, "entities")),
      };
    }
    case "tool_failed": {
      const tool = asString(raw.tool);
      if (!tool) return null;
      const staffOnly = dualKey(raw, "staff_only");
      const staff = isObject(staffOnly) ? staffOnly : {};
      return {
        id,
        ts,
        kind: "tool_failed",
        parentId: asString(dualKey(raw, "parent_id")) ?? tool,
        errorClass:
          asString(dualKey(staff, "error_class")) ??
          asString(dualKey(raw, "error_class")) ??
          "UnknownError",
        userMessage:
          asString(dualKey(raw, "user_facing")) ??
          asString(dualKey(raw, "user_message")) ??
          "Couldn't complete this step.",
      };
    }
    default:
      return null;
  }
};

/**
 * Back-compat alias for the original replay-path entry point. New code
 * should call ``parseTraceEvent`` directly.
 */
export const wireToToolTraceEvent = parseTraceEvent;

/**
 * Bulk codec — convenience for the replay path which always processes
 * an array of persisted events at once. Drops malformed entries
 * silently; the reducer's exhaustive default trips on any truly
 * unrecognized ``kind`` slipping through.
 */
export const normalizePersistedTrace = (
  raw: readonly unknown[],
): readonly ToolTraceEvent[] =>
  raw
    .map((evt, i) => parseTraceEvent(evt, TS_EPOCH + i))
    .filter((e): e is ToolTraceEvent => e !== null);
